import { useOrganizationAvailableForInvitationUsers } from '@forwardflow/hooks/customHooks/organization/useGetAvailableForInvitationUsers'
import { userOrganizationUsersForInvitationFilter } from '@forwardflow/store/filter/userOrganizationUsersForInvitationFilter'
import { Button } from '@forwardflow/ui/Button'
import { SearchField } from '@forwardflow/ui/SearchField'
import { message, Modal } from 'antd'
import { useEffect, useMemo, useRef, useState, useCallback } from 'react'
import UserListItem from './UserListItem'
import { UserService } from '@forwardflow/services/UserService'
import {
   useAreYouAnAgentModalStore,
   useInviteAgentModalStore,
} from '@forwardflow/store/modal'
import debounce from 'lodash/debounce'

import { Spinner } from '@forwardflow/ui'
import Icon from '@forwardflow/ui/Icons'

export const InviteAgentModal = () => {
   const [isInviteSent, setIsInviteSent] = useState(false)
   const [isLoading, setIsLoading] = useState(false)
   const [isSubmitLoading, setIsSubmitLoading] = useState(false)
   const [isSearching, setIsSearching] = useState(false)
   const { isInviteAgentModalOpen, closeInviteAgentModal } =
      useInviteAgentModalStore()
   const [selectedUsers, setSelectedUsers] = useState<any[]>([])
   const [searchQuery, setSearchQuery] = useState('')
   const [selectedRole, setSelectedRole] = useState<'Admin' | 'Agent'>('Admin')

   const { openAreYouAnAgentModal } = useAreYouAnAgentModalStore()
   const { page, limit, search, setQueryParams } = userOrganizationUsersForInvitationFilter()
   const { data, mutate, isLoading: isApiLoading } = useOrganizationAvailableForInvitationUsers({
      page: page,
      limit: limit,
      search: search, 
   })

   const [allUsers, setAllUsers] = useState<any[]>([])

   useEffect(() => {
      if (isInviteAgentModalOpen) {
         setIsInviteSent(false)
         setSelectedUsers([])
         setIsLoading(false)
         setIsSubmitLoading(false)
         setIsSearching(false)
         setAllUsers([])
         setSearchQuery('')
         setSelectedRole('Admin')
         setQueryParams({ page: 1, limit, search: '' })
      }
   }, [isInviteAgentModalOpen, setQueryParams, limit])

   useEffect(() => {
      if (isInviteAgentModalOpen && page === 1) {
         const timer = setTimeout(() => {
            mutate()
         }, 50)
         return () => clearTimeout(timer)
      }
   }, [isInviteAgentModalOpen, page, mutate])

   useEffect(() => {
      if (data?.data && isInviteAgentModalOpen) {
         setAllUsers((prevUsers) => {
            if (page === 1) {
               return data.data
            }
            const combined = [...prevUsers, ...data.data]
            const unique = combined.filter(
               (user, index, self) =>
                  index === self.findIndex((u) => u.email === user.email)
            )
            return unique
         })
         setIsLoading(false)
         setIsSearching(false) 
      }
   }, [data, isInviteAgentModalOpen, page])

   useEffect(() => {
      if (isApiLoading && searchQuery) {
         setIsSearching(true)
      } else if (!isApiLoading) {
         setIsSearching(false)
      }
   }, [isApiLoading, searchQuery])

   useEffect(() => {
      if (isInviteAgentModalOpen && (page ?? 1) > 1) {
         mutate()
      }
   }, [page, isInviteAgentModalOpen, mutate])

   const debouncedSearch = useCallback(
      debounce((value: string) => {
         setIsSearching(true)
         setQueryParams({ 
            page: 1, 
            limit, 
            search: value,
            sorts: '',
            fields: '',
            filters: '',
            includes: ''
         })
         setAllUsers([])
      }, 500),
      [setQueryParams, limit]
   )

   const handleSearch = (value: string) => {
      setSearchQuery(value)
      if (value.trim()) {
         debouncedSearch(value)
      } else {
         setIsSearching(false)
         setQueryParams({ 
            page: 1, 
            limit, 
            search: '',
            sorts: '',
            fields: '',
            filters: '',
            includes: ''
         })
         setAllUsers([])
      }
   }

   const scrollContainerRef = useRef<HTMLDivElement>(null)

   const handleScroll = async (e: React.UIEvent<HTMLDivElement>) => {
      const element = e.target as HTMLDivElement
      const hasReachedBottom =
         element.scrollHeight - element.scrollTop === element.clientHeight
      const hasMoreData = allUsers.length < (data?.meta?.totalItems ?? 0)
      
      if (hasReachedBottom && hasMoreData && !isLoading) {
         setIsLoading(true)
         setQueryParams({ 
            page: (page ?? 0) + 1, 
            limit,
            search,
            sorts: '',
            fields: '',
            filters: '',
            includes: ''
         })
      }
   }

   const users = useMemo(() => {
      return allUsers.filter(
         (user: any) =>
            user.invitationStatus === null ||
            user.invitationStatus === 'pending'
      )
   }, [allUsers])

   const handleUserSelect = (user: any) => {
      setSelectedUsers((prevUsers) => {
         const isUserSelected = prevUsers.some((u) => u.email === user.email)
         if (isUserSelected) {
            return prevUsers.filter((u) => u.email !== user.email)
         } else {
            return [...prevUsers, user]
         }
      })
   }

   const sendUserInvite = async (emailIds: string[], role: string) => {
      setIsSubmitLoading(true)
      try {
         await UserService.userSendInvitation(emailIds, role)
         message.success('User invitation sent successfully')
         setIsInviteSent(true)
      } catch (error) {
         console.error(error)
         message.error('Failed to send user invitation')
      } finally {
         setIsSubmitLoading(false)
      }
   }

   const handleSubmit = () => {
      if (selectedUsers.length > 0) {
         const userEmails = selectedUsers.map((user: any) => user.email)
         sendUserInvite(userEmails, selectedRole)
      }
   }

   const InviteSentComponent = () => {
      return (
         <div className="flex flex-col gap-y-5 !bg-tile_1_bg_img">
            <div className="w-[100px] h-[100px] rounded-full bg-primary/40 flexc mx-auto mt-[110px]">
               <Icon name="Check" className="scale-[5] fill-white" />
            </div>
            <p className="mb-20 text-[24px] font-semibold text-center">
               User Invites sent!
            </p>
         </div>
      )
   }

   return (
      <>
         <Modal
            className={`${!isInviteSent ? '' : 'p-0'}  ff-modal lg:!w-[560px] `}
            title={`${
               !isInviteSent ? 'Select agents to send email invites' : ''
            }`}
            open={isInviteAgentModalOpen}
            onCancel={closeInviteAgentModal}
            footer={null}
         >
            <>
               {isInviteSent ? (
                  <InviteSentComponent />
               ) : (
                  <>
                     <p className="text-[#818181] text-[12px] font-medium">
                        Select agents to send email invites
                     </p>

                     <div className="mt-4 mb-4">
                        <p className="text-[#818181] text-[12px] font-medium mb-3">
                           Select Role
                        </p>
                        <div className="flex gap-4 mb-4">
                           <label className="flex items-center gap-2 cursor-pointer">
                              <input
                                 type="radio"
                                 name="role"
                                 value="Admin"
                                 checked={selectedRole === 'Admin'}
                                 onChange={(e) => setSelectedRole(e.target.value as 'Admin' | 'Agent')}
                                 className="w-4 h-4 accent-primary cursor-pointer"
                              />
                              <span className="text-[14px] font-medium text-white">Admin</span>
                           </label>
                           <label className="flex items-center gap-2 cursor-pointer">
                              <input
                                 type="radio"
                                 name="role"
                                 value="Agent"
                                 checked={selectedRole === 'Agent'}
                                 onChange={(e) => setSelectedRole(e.target.value as 'Admin' | 'Agent')}
                                 className="w-4 h-4 accent-primary cursor-pointer"
                              />
                              <span className="text-[14px] font-medium text-white">Agent</span>
                           </label>
                        </div>
                     </div>

                     <div className="mb-4">
                        <SearchField
                           placeholder="Search Users"
                           className="!rounded-lg !text-[14px] !w-full !max-w-full !bg-white/5 !border !border-white/10"
                           value={searchQuery}
                           onChange={handleSearch}
                        />
                     </div>

                     <p className="mb-4 text-[10px] text-[#818181]">
                        Selected {selectedUsers.length}/{users.length} Agents
                     </p>
                     <div
                        ref={scrollContainerRef}
                        className="flex flex-col overflow-x-auto gap-y-1 max-h-[378px]"
                        onScroll={handleScroll}
                     >
                        {isSearching ? (
                           <div className="flex items-center justify-center py-8">
                              <Spinner />
                           </div>
                        ) : searchQuery && users?.length === 0 ? (
                           <div className="text-center py-8 text-white/70">No results found</div>
                        ) : users?.length > 0 ? (
                           users.map((data, index) => (
                              <UserListItem
                                 key={index}
                                 user={data}
                                 isSelected={selectedUsers.includes(data)}
                                 onUserSelect={() => {
                                    if (data.isAdmin) {
                                       const userToHandle = data
                                       const isCurrentlySelected =
                                          selectedUsers.includes(userToHandle)

                                       openAreYouAnAgentModal()

                                       const unsubscribe =
                                          useAreYouAnAgentModalStore.subscribe(
                                             (state: any) => {
                                                if (
                                                   !state.isAreYouAnAgentModalOpen
                                                ) {
                                                   if (
                                                      state.isUserAgent &&
                                                      !isCurrentlySelected
                                                   ) {
                                                      handleUserSelect(
                                                         userToHandle
                                                      )
                                                   } else if (
                                                      !state.isUserAgent &&
                                                      isCurrentlySelected
                                                   ) {
                                                      setSelectedUsers((prev) =>
                                                         prev.filter(
                                                            (user) =>
                                                               user.id !==
                                                               userToHandle.id
                                                         )
                                                      )
                                                   }
                                                   unsubscribe()
                                                }
                                             }
                                          )
                                    } else {
                                       handleUserSelect(data)
                                    }
                                 }}
                              />
                           ))
                        ) : (
                           <div className="text-center">No users to invite</div>
                        )}
                        {isLoading && !isSearching && (
                           <div className="text-center">
                              <Spinner />
                           </div>
                        )}
                     </div>
                     <Button
                        onClick={handleSubmit}
                        isLoading={isSubmitLoading}
                        isDisabled={isLoading}
                        className="!mt-8"
                     >
                        Send Invites
                     </Button>
                  </>
               )}
            </>
         </Modal>
      </>
   )
}
